import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ALL_PROJECTS_VIEW_ID, useFilterContext } from "@app/network-portfolio-management/contexts/FilterContext";
import { ManageViewsModal } from "@app/network-portfolio-management/components/ManageViewsModal";
import { SaveAsViewModal } from "@app/network-portfolio-management/components/SaveAsViewModal";
import { DefaultViewBadge } from "@app/network-portfolio-management/components/DefaultViewBadge";
import { NPMQesViewBadge } from "@app/network-portfolio-management/components/NPMQesViewBadge";
import { hasActiveFilters } from "@app/network-portfolio-management/utils/savedViewsUtils";
import { useSaveViewHandler } from "@app/network-portfolio-management/hooks/useSaveViewHandler";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { EllipsisTooltip } from "@app/shared/EllipsisTooltip";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";

export const SavedViewsButton: FC = () => {
  const { activeFilters, activeSavedViewId, applyView, clearActiveView, defaultViewId, error, loading, savedViews } =
    useFilterContext();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [manageModalOpen, setManageModalOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const hasAnyFilter = hasActiveFilters(activeFilters);

  const activeView = savedViews.find((v) => v.npmFilteredViewId === activeSavedViewId) ?? null;

  const viewLabel = useMemo(
    () => (activeView ? activeView.name : hasAnyFilter ? "Custom" : "All Projects"),
    [activeView, hasAnyFilter],
  );

  const canSave = hasAnyFilter && activeSavedViewId === null && !loading;

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current && !dropdownRef.current.contains(target) &&
        buttonRef.current && !buttonRef.current.contains(target)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [dropdownOpen]);

  const handleViewClick = useCallback((id: number) => {
    if (activeSavedViewId === id) {
      clearActiveView();
    } else {
      applyView(id);
    }
    setDropdownOpen(false);
  }, [activeSavedViewId, applyView, clearActiveView]);

  const handleSaveCurrentView = useCallback(() => {
    setDropdownOpen(false);
    setSaveModalOpen(true);
  }, []);

  const handleManageViews = useCallback(() => {
    setDropdownOpen(false);
    setManageModalOpen(true);
  }, []);

  const handleSaved = useSaveViewHandler();

  const dropdownStyle = useMemo(() => {
    if (!dropdownOpen || !buttonRef.current) return undefined;
    const rect = buttonRef.current.getBoundingClientRect();
    return { left: rect.left, position: "fixed" as const, top: rect.bottom + 4 };
  }, [dropdownOpen]);

  return (
    <>
      <div className="qes-npm-saved-views">
        <span className="qes-npm-saved-views__label">
          <span className="qes-text-input__label">Saved Views</span>
          <TooltipWrapper position="top" text="Saved Views save your current filter selections so you can quickly return to them later. Sorting and grouping options are not included in a Saved View." tooltipWidth="20rem">
            <QesIcon description="Info" iconName={QesIcons.InfoCircle} width="16" testId="npm-saved-views-info-icon" />
          </TooltipWrapper>
        </span>
        <QESButton
          qesRef={buttonRef}
          qesTestId="npm-saved-views-btn"
          qesType="none"
          aria-controls="npm-sv-list"
          aria-expanded={dropdownOpen}
          aria-haspopup="listbox"
          className="qes-npm-saved-views__btn"
          onClick={() => setDropdownOpen((prev) => !prev)}
        >
          <EllipsisTooltip text={viewLabel} focusable={false} />
        </QESButton>
      </div>

      {dropdownOpen && dropdownStyle && (
        <div
          ref={dropdownRef}
          className="qes-npm-saved-views-dropdown"
          data-testid="npm-sv-dropdown"
          id="npm-sv-dropdown"
          style={dropdownStyle}
        >
          <div className="qes-npm-saved-views-dropdown__header">Saved Views</div>

          {error && (
            <div className="qes-npm-saved-views-dropdown__error" data-testid="npm-sv-error">
              <QESAlert type="danger" persist>
                {error}
              </QESAlert>
            </div>
          )}

          {!error && (
            <>
              {loading && (
                <div className="qes-npm-saved-views-dropdown__loading" data-testid="npm-sv-loading">Loading...</div>
              )}
              {!loading && (
                <div className="qes-npm-saved-views-dropdown__list" id="npm-sv-list" role="listbox" aria-label="Saved views" data-testid="npm-sv-listbox">
                  {savedViews.map((view) => {
                    // When no view is explicitly applied, the page is showing All Projects implicitly,
                    // so the All Projects row should still read as the selected radio.
                    const isActive = activeSavedViewId === view.npmFilteredViewId
                      || (activeSavedViewId === null && view.npmFilteredViewId === ALL_PROJECTS_VIEW_ID);
                    return (
                    <QESButton
                      key={view.npmFilteredViewId}
                      qesTestId={`npm-sv-item-${view.npmFilteredViewId}`}
                      qesType="none"
                      aria-selected={isActive}
                      className={`qes-npm-saved-views-item${isActive ? " qes-npm-saved-views-item-active" : ""}`}
                      onClick={() => handleViewClick(view.npmFilteredViewId)}
                      role="option"
                    >
                      <span className="qes-npm-saved-views-item__radio" aria-hidden="true" />
                      <span className="qes-npm-saved-views-item__info">
                        <span className="qes-npm-saved-views-item__name">
                          {view.name}
                          {view.isQesView && <NPMQesViewBadge />}
                          {(view.npmFilteredViewId === defaultViewId || (defaultViewId === null && view.npmFilteredViewId === ALL_PROJECTS_VIEW_ID)) && (
                            <DefaultViewBadge />
                          )}
                        </span>
                        {view.description && (
                          <span
                            className="qes-npm-saved-views-item__description"
                            data-testid={`npm-sv-description-${view.npmFilteredViewId}`}
                          >
                            {view.description}
                          </span>
                        )}
                      </span>
                    </QESButton>
                    );
                  })}
                </div>
              )}
            </>
          )}

          <div className="qes-npm-saved-views-dropdown__footer">
            <QESButton
              qesTestId="npm-sv-save-current"
              qesType="none"
              className={`qes-npm-saved-views-dropdown__action${canSave ? "" : " qes-npm-saved-views-dropdown__action-disabled"}`}
              disabled={!canSave}
              onClick={handleSaveCurrentView}
            >
              Save current view…
            </QESButton>
            <QESButton
              qesTestId="npm-sv-manage"
              qesType="none"
              className="qes-npm-saved-views-dropdown__action"
              onClick={handleManageViews}
            >
              Manage views
            </QESButton>
          </div>
        </div>
      )}

      <SaveAsViewModal
        open={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        onSaved={handleSaved}
      />

      <ManageViewsModal
        open={manageModalOpen}
        onClose={() => setManageModalOpen(false)}
      />
    </>
  );
};
